import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { MemberNameLink } from '@/components/shared/member-name-link';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime, formatMoney } from '@/utils/format';

export interface ExpenseDetail {
  id: string;
  payee: string;
  payeeKind: 'internal' | 'external' | 'other';
  memberId: string | null;
  amountMinor: number;
  currency: string;
  expenseDate: string;
  status: string;
  categoryId: string;
  categoryName: string | null;
  fundId: string;
  fundName: string | null;
  description: string | null;
  method: string;
  reference: string | null;
  approvedAt: string | null;
  voidReason: string | null;
  createdAt: string;
}

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1 border-b border-border py-3 last:border-b-0 sm:grid-cols-[9rem_1fr] sm:gap-4">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body text-fg">{children}</dd>
  </div>
);

const statusBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'brand';
    case 'pending_approval':
      return 'warning';
    case 'rejected':
    case 'void':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const ExpenseDetailBody = ({
  expenseId,
  linkOut = true,
  showIntro = true,
}: {
  expenseId: string;
  linkOut?: boolean;
  showIntro?: boolean;
}) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);

  const detail = useQuery({
    queryKey: ['expenses', expenseId],
    queryFn: () => api.get<ExpenseDetail>(`/expenses/${expenseId}`),
    enabled: Boolean(expenseId),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const row = detail.data;
  if (!row) {
    return <p className="text-body-sm text-fg-secondary">{t('expenses.detail.notFoundBody')}</p>;
  }

  const expenseDate = formatDate(row.expenseDate, { locale, style: 'medium' });

  const payeeNode =
    row.payeeKind === 'internal' && row.memberId && linkOut ? (
      <MemberNameLink memberId={row.memberId} name={row.payee} className="font-medium" />
    ) : (
      row.payee
    );

  const fundNode = row.fundName ? (
    linkOut ? (
      <Link to={`/giving/funds/${row.fundId}`} className="font-medium text-brand hover:underline">
        {row.fundName}
      </Link>
    ) : (
      row.fundName
    )
  ) : (
    tCommon('state.loading')
  );

  return (
    <div className="space-y-3">
      {showIntro ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-body font-medium text-fg">{row.payee}</p>
            <Badge variant={statusBadgeVariant(row.status)}>
              {t(`expenses.status.${row.status}`, { defaultValue: row.status })}
            </Badge>
          </div>
          <p className="text-body-sm text-fg-secondary">
            {t('expenses.detail.description', {
              amount: formatMoney(row.amountMinor, row.currency, { locale }),
              category: row.categoryName ?? tCommon('state.loading'),
              date: expenseDate,
            })}
          </p>
        </>
      ) : null}

      <dl>
        <DetailRow label={t('expenses.columns.date')}>{expenseDate}</DetailRow>
        <DetailRow label={t('expenses.columns.amount')}>
          {formatMoney(row.amountMinor, row.currency, { locale })}
        </DetailRow>
        <DetailRow label={t('expenses.columns.payee')}>{payeeNode}</DetailRow>
        <DetailRow label={t('expenses.payeeType')}>
          {t(`expenses.payeeKinds.${row.payeeKind}`)}
        </DetailRow>
        <DetailRow label={t('expenses.category')}>
          {row.categoryName ?? tCommon('state.loading')}
        </DetailRow>
        <DetailRow label={t('expenses.fund')}>{fundNode}</DetailRow>
        <DetailRow label={t('expenses.columns.status')}>
          {t(`expenses.status.${row.status}`, { defaultValue: row.status })}
        </DetailRow>
        {row.method ? (
          <DetailRow label={t('expenses.detail.method')}>
            {t(`expenses.methods.${row.method}`, { defaultValue: row.method })}
          </DetailRow>
        ) : null}
        {row.reference ? (
          <DetailRow label={t('expenses.detail.reference')}>{row.reference}</DetailRow>
        ) : null}
        {row.description ? (
          <DetailRow label={t('expenses.memo')}>{row.description}</DetailRow>
        ) : null}
        <DetailRow label={t('expenses.detail.recorded')}>
          {formatDateTime(row.createdAt, { locale, timezone, withZone: true })}
        </DetailRow>
        {row.approvedAt ? (
          <DetailRow label={t('expenses.detail.approvedAt')}>
            {formatDateTime(row.approvedAt, { locale, timezone, withZone: true })}
          </DetailRow>
        ) : null}
        {row.status === 'void' ? (
          <DetailRow label={t('expenses.detail.voidReason')}>
            {row.voidReason?.trim() || t('expenses.detail.voidReasonEmpty')}
          </DetailRow>
        ) : null}
      </dl>
    </div>
  );
};
